import type React from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "@coval/ui"

interface ConfirmationDialogProps {
  showConfirmDialog: boolean
  title: string
  description: React.ReactNode
  setShowConfirmDialog: (show: boolean) => void
  handleConfirmChange: () => void
  handleCancelChange?: () => void
  cancelLabel?: string
  confirmLabel?: string
  /**
   * Marks the confirmed action as in flight: the confirm button shows a
   * spinner, cancel is disabled, and the dialog cannot be dismissed. Passing
   * this prop (even `false`) also stops confirm from auto-closing the dialog —
   * the caller closes it once the action settles.
   */
  pending?: boolean
}

export const ConfirmationDialog = ({
  showConfirmDialog,
  title,
  description,
  setShowConfirmDialog,
  handleConfirmChange,
  handleCancelChange,
  cancelLabel = "Cancel",
  confirmLabel = "Continue",
  pending,
}: ConfirmationDialogProps) => {
  const callerManagesClose = pending !== undefined
  return (
    <AlertDialog
      open={showConfirmDialog}
      onOpenChange={(open) => {
        if (!pending) {
          setShowConfirmDialog(open)
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-4">
          <AlertDialogCancel asChild>
            <Button
              variant="secondary"
              onClick={handleCancelChange}
              disabled={pending}
            >
              {cancelLabel}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              loading={pending}
              onClick={(event) => {
                if (callerManagesClose) {
                  event.preventDefault()
                }
                handleConfirmChange()
              }}
            >
              {confirmLabel}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
